import { Injectable, TemplateRef } from "@angular/core";
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { ReplaySubject, Subscription, Subject } from "rxjs";
import { NavigationItem } from "@common/data-models/service/navigation-item";
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { CustomCardFieldsService } from "@common/services/custom-card-fields-service";
import { FieldDef } from "@common/data-models/service/field-def";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { ViewStateFilterStates } from "@common/enums/viewstate-filter-states";
import { ViewStateRecord } from "@common/data-models/records/viewstate-record";
import { Record } from "@common/data-models/records/record";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { TopicViewStateRecord } from "@common/data-models/records/topic-viewstate-record";
import { WireViewStateRecord } from "@common/data-models/records/wire-viewstate-record";
import { SingleTopicViewStateRecord } from "@common/data-models/records/single-topic-viewstate-record";
import { AssignmentViewStateRecord } from "@common/data-models/records/assignment-viewstate-record";
import { SearchViewStateRecord } from "@common/data-models/records/search-viewstate-record";
import { SlideshowViewStateRecord } from "@common/data-models/records/slideshow-viewstate-record"
import { TopicLayoutOptionsConst } from "@common/utils/util";
import { RecordFactory } from "@common/data-models/records/record-factory";
import { ViewStateSortOders } from "@common/enums/viewstate-sort-orders";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { ViewStateFilterVisibility } from "@common/enums/viewstate-filter-visibility";

import { LanguageSettings } from "@common/services/language-settings";
import { SlideshowService } from "@common/services/slideshow-service";
import { PublicationChannelsService } from "@common/services/publication-channels-service";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { PlanningViewsResults } from "@common/data-models/navigation/planning-views-results";
import { ServiceRecord } from "@common/data-models/service/service-record";
import { DataStore } from "@common/data-models/data-store/data-store";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { BasicContentService } from "@common/services/basic-content-service";
import { DraftViewStateRecord } from "@common/data-models/records/draft-viewstate-record";
import { RundownListViewStateRecord } from "@common/data-models/records/rundown-list-viewstate-record";
import { PublicationViewStateRecord } from "@common/data-models/records/publication-viewstate-record";
import { DateTime } from "@common/data-models/complex-types/date-time";

import { Util } from "@common/utils/util";
import { ResultGroupStaticServices } from '@common/data-models/search-results/result-group';
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateFilter } from "@common/data-models/navigation/viewstate-filter";
import { SingleFeedViewStateRecord } from "@common/data-models/records/single-feed-viewstate-record";
import { RSSFeedViewStateRecord } from "@common/data-models/records/rss-feed-viewstate-record";
import { TwitterFeedViewStateRecord } from "@common/data-models/records/twitter-feed-viewstate-record";
import { PublicationChannelRecord } from "../data-models/records/publication-channel-record";
import { ViewStateDelay } from "@common/data-models/navigation/viewstate-delay";
import { ViewStateSequence } from "@common/data-models/navigation/viewstate-sequence";
import { VisualStateService } from "@common/services/visual-state-service";

export type ViewStateRecordsAlias = ViewStateRecord | TopicViewStateRecord | AssignmentViewStateRecord | SearchViewStateRecord | SlideshowViewStateRecord | SingleTopicViewStateRecord | WireViewStateRecord | DraftViewStateRecord | RundownListViewStateRecord | PublicationViewStateRecord | SingleFeedViewStateRecord | RSSFeedViewStateRecord;

@Injectable({ providedIn: 'root' })
export class LayoutState {

    FilterVisibility: ViewStateFilterVisibility = ViewStateFilterVisibility.HIDDEN;

    // Current view state record ex: navigation item view state is SlideshowViewStateRecord, but current view state is TopicViewStateRecord
    ViewStateRecord: ViewStateRecordsAlias = null;
    ViewStateRecordSubject = new ReplaySubject<ViewStateRecordsAlias>(1);
    ViewStateRecordObservable = this.ViewStateRecordSubject.asObservable();

    // Navigation item view state record
    NavigationViewStateRecord: ViewStateRecordsAlias = null;
    NavigationViewStateRecordSubject = new ReplaySubject<ViewStateRecordsAlias>(1);
    NavigationViewStateRecordObservable = this.NavigationViewStateRecordSubject.asObservable();

    private ViewStateRecordPropertySubscription: Subscription = null;
    private SlideShowViewStateRecordSubscription: Subscription = null;
    ViewStateRecordPropertySubject = new Subject<[string, any, any]>();
    ViewStateRecordPropertyObservable = this.ViewStateRecordPropertySubject.asObservable();

    ViewStateFilterLabel: string = "Filters";
    ViewStatePrevFilterState: ViewStateFilterStates;
    PlanningViewsFilterState: ViewStateFilterStates = ViewStateFilterStates.Collapsed;

    inSlideshowMode: boolean = false;
    inEditTeamSettingsMode: boolean = false;

    applicationName: string;


    mainCta: TemplateRef<unknown>;
    secondaryCta: TemplateRef<unknown>;
    viewOptions: TemplateRef<unknown>;
    rightSlot: TemplateRef<unknown>;

    constructor(
        private nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public slideshowService: SlideshowService,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService,
        public customCardFieldsService: CustomCardFieldsService,
        public publicationChannelsService: PublicationChannelsService,
        private visualStateService: VisualStateService
    ) {
        ResultGroupStaticServices.viewStateService = this;
    }


    //ViewStateRecord Getters/Setters
    get viewStateSupportsLayout(): boolean {
        switch (this.ViewStateRecord?.viewStateType) {
            case ViewStateTypes.TOPIC:
                //case ViewStateTypes.ASSIGNMENT:
                return true
            default: return false;
        }
    }

    get viewStateSupportsGroupBy(): boolean {
        switch (this.ViewStateRecord?.viewStateType) {
            case ViewStateTypes.ASSIGNMENT:
            case ViewStateTypes.TOPIC:
                // case ViewStateTypes.WIRE:
                return true
            default: return false;
        }
    }

    get viewStateSupportsGroupByOrder(): boolean {
        switch (this.ViewStateRecord?.viewStateType) {
            case ViewStateTypes.ASSIGNMENT:
            case ViewStateTypes.TOPIC:
                // case ViewStateTypes.WIRE:
                return true
            default: return false;
        }
    }

    get viewStateGroupByOrder(): ViewStateSortOders | null {
        if (this.ViewStateRecord instanceof TopicViewStateRecord ||
            this.ViewStateRecord instanceof AssignmentViewStateRecord) {

            return this.ViewStateRecord.viewStateGroupSortOrder;
        }
        return ViewStateSortOders.ASC;
    }

    get viewStateGroupByOrderDisabled(): boolean {
        if (this.navigationViewStateType === ViewStateTypes.SLIDESHOW ||
            this.viewStateLayout?.Value === ViewStateLayouts.MAP ||
            this.viewStateLayout?.Value === ViewStateLayouts.TILE ||
            this.viewStateSortingManual) {

            return true;
        }
        return false;
    }


    get viewStateSupportsSortBy(): boolean {
        switch (this.ViewStateRecord?.viewStateType) {
            case ViewStateTypes.ASSIGNMENT:
            case ViewStateTypes.TOPIC:
            case ViewStateTypes.WIRE:
            case ViewStateTypes.SEARCH:
                return true
            default: return false;
        }
    }

    get viewStateSupportsSortByOrder(): boolean {
        switch (this.ViewStateRecord?.viewStateType) {
            case ViewStateTypes.ASSIGNMENT:
            case ViewStateTypes.TOPIC:
            case ViewStateTypes.WIRE:
            case ViewStateTypes.SEARCH:
                return true
            default: return false;
        }
    }

    get viewStateSortOrder(): ViewStateSortOders | null {
        if (this.ViewStateRecord instanceof TopicViewStateRecord ||
            this.ViewStateRecord instanceof AssignmentViewStateRecord ||
            this.ViewStateRecord instanceof SearchViewStateRecord) {

            return this.ViewStateRecord.viewStateSortOrder;
        }
        return ViewStateSortOders.ASC;
    }

    get viewStateSortDisabled(): boolean {
        if (this.navigationViewStateType === ViewStateTypes.SLIDESHOW ||
            this.viewStateLayout?.Value === ViewStateLayouts.MAP ||
            this.viewStateSortingManual) {

            return true;
        }
        return false;
    }

    get viewStateLayout(): ViewStateOptions | null {
        if (this.ViewStateRecord instanceof TopicViewStateRecord
            || this.ViewStateRecord instanceof AssignmentViewStateRecord
            || this.ViewStateRecord instanceof WireViewStateRecord
            || this.ViewStateRecord instanceof DraftViewStateRecord
            || this.ViewStateRecord instanceof RundownListViewStateRecord
            || this.ViewStateRecord instanceof PublicationViewStateRecord) {
            if (this.visualStateService.isLessThanSmall) {
                return null;
            }
            return this.ViewStateRecord.viewStateLayout;
        }
        return null;
    }

    get viewStateSorting(): ViewStateOptions | null {
        if (this.ViewStateRecord instanceof TopicViewStateRecord
            || this.ViewStateRecord instanceof WireViewStateRecord
            || this.ViewStateRecord instanceof AssignmentViewStateRecord
            || this.ViewStateRecord instanceof DraftViewStateRecord
            || this.ViewStateRecord instanceof RundownListViewStateRecord 
            || this.ViewStateRecord instanceof SearchViewStateRecord) {
            return this.ViewStateRecord.viewStateSorting;
        }
        return null;
    }

    get viewStateSortingManual(): boolean {
        if (this.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder) {
            return true;
        }
        return false;
    }

    get viewStateGrouping(): ViewStateOptions | null {
        if (this.ViewStateRecord instanceof SingleViewStateRecord) {
            return this.ViewStateRecord.viewStateGrouping;
        }
        return null;
    }

    get viewStateHasFilter(): boolean {
        return (!(this.ViewStateRecord instanceof SingleTopicViewStateRecord
            || this.ViewStateRecord instanceof PublicationViewStateRecord));
    }

    //TODO: FIGURE OUT HOW THIS SHOULDN'T BE ANY
    set viewStateLayout(value: any) {
        if (this.ViewStateRecord instanceof TopicViewStateRecord
            || this.ViewStateRecord instanceof AssignmentViewStateRecord
            || this.ViewStateRecord instanceof WireViewStateRecord
            || this.ViewStateRecord instanceof DraftViewStateRecord
            || this.ViewStateRecord instanceof RundownListViewStateRecord
            || this.ViewStateRecord instanceof PublicationViewStateRecord) {
            this.ViewStateRecord.viewStateLayout.Value = value;
        }
    }

    get viewStateCustomCardItems(): FieldDef[] | null {
        if (this.ViewStateRecord instanceof SingleViewStateRecord ||
            this.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.ViewStateRecord.viewStateCustomCardItems;
        }
        return null;
    }


    get viewStatePublicationRevisionNumber(): number | null {
        if (this.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.ViewStateRecord.publicationRevisionNumber;
        }
        return null;
    }

    set viewStatePublicationRevisionNumber(value: number | null) {
        if (this.ViewStateRecord instanceof PublicationViewStateRecord) {
            this.ViewStateRecord.publicationRevisionNumber = value;
        }
    }

    get viewStateMaxPublicationRevisionNumber(): number | null {
        if (this.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.ViewStateRecord.maxPublicationRevisionNumber;
        }
        return null;
    }

    get viewStatePublicationChannel(): PublicationChannelRecord | null {
        if (this.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.ViewStateRecord.publicationChannel;
        }
        return null;
    }

    get viewStatePublicationRevisionsList(): { id: number; title: string }[] | null {
        if (this.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.ViewStateRecord.publicationRevisionsList;
        }
        return null;
    }

    //NavigationViewState Getters/Setters
    get navigationViewStateFilter(): ViewStateFilter | null {
        if (this.NavigationViewStateRecord instanceof SingleViewStateRecord) {
            return this.NavigationViewStateRecord.viewStateFilter;
        }
        return null;
    }

    get navigationViewStateType(): ViewStateTypes {
        return this.NavigationViewStateRecord.viewStateType;
    }

    get navigationViewStateviewStateGuid(): string | null {
        if (this.NavigationViewStateRecord instanceof PublicationViewStateRecord) {
            return this.NavigationViewStateRecord.viewStateGUID;
        }
        return null;
    }

    set navigationViewStateviewStateGuid(value: string) {
        if (this.NavigationViewStateRecord instanceof PublicationViewStateRecord) {
            this.NavigationViewStateRecord.viewStateGUID = value;
        }
    }

    get navigationViewStateSlideshowDelay(): ViewStateDelay | null {
        if (this.NavigationViewStateRecord instanceof SlideshowViewStateRecord) {
            return this.NavigationViewStateRecord.viewStateSlideshowDelay;
        }
        return null;
    }

    get navigationViewStateSlideshowSequence(): ViewStateSequence | null {
        if (this.NavigationViewStateRecord instanceof SlideshowViewStateRecord) {
            return this.NavigationViewStateRecord.viewStateSlideshowSequence;
        }
        return null;
    }

    get viewStateSearchQuery(): string {
        if (this.ViewStateRecord instanceof SearchViewStateRecord) {
            return this.ViewStateRecord.viewStateSearchQuery;
        }
        return "";
    }


    public toggleFilterState() {
        if (this.NavigationViewStateRecord) {
            this.NavigationViewStateRecord.viewStateFilterState = !this.NavigationViewStateRecord.viewStateFilterState;
        }
    }

    public togglePlanningViewsFilterState() {
        if (this.PlanningViewsFilterState == ViewStateFilterStates.Expanded) {
            this.PlanningViewsFilterState = ViewStateFilterStates.Collapsed;
        } else {
            this.PlanningViewsFilterState = ViewStateFilterStates.Expanded;
        }
    }

    public toggleSortOrder() {
        if (this.ViewStateRecord) {
            if ((<SingleViewStateRecord>this.ViewStateRecord).viewStateSortOrder == ViewStateSortOders.ASC) {
                (<SingleViewStateRecord>this.ViewStateRecord).viewStateSortOrder = ViewStateSortOders.DESC;
            } else {
                (<SingleViewStateRecord>this.ViewStateRecord).viewStateSortOrder = ViewStateSortOders.ASC;
            }
        }
    }

    public toggleGroupSortOrder() {
        if (this.ViewStateRecord) {
            if ((<SingleViewStateRecord>this.ViewStateRecord).viewStateGroupSortOrder == ViewStateSortOders.ASC) {
                (<SingleViewStateRecord>this.ViewStateRecord).viewStateGroupSortOrder = ViewStateSortOders.DESC;
            } else {
                (<SingleViewStateRecord>this.ViewStateRecord).viewStateGroupSortOrder = ViewStateSortOders.ASC;
            }
        }
    }

    public getGrouping() {
        if (this.ViewStateRecord && this.ViewStateRecord instanceof SingleViewStateRecord) {
            return this.ViewStateRecord.viewStateGrouping.Value;
        }
        return null;
    }

    public getSorting() {
        if (this.ViewStateRecord && this.ViewStateRecord instanceof SingleViewStateRecord) {
            return this.ViewStateRecord.viewStateSorting.Value;
        }
        return null;
    }

    public getSortOrder() {
        if (this.ViewStateRecord && this.ViewStateRecord instanceof SingleViewStateRecord) {
            return this.ViewStateRecord.viewStateSortOrder;
        }
        return null;
    }

    public getGroupSortOrder() {
        if (this.ViewStateRecord && this.ViewStateRecord instanceof SingleViewStateRecord) {
            return this.ViewStateRecord.viewStateGroupSortOrder;
        }
        return null;
    }

    public resetLayout() {
        if (this.ViewStateRecord && this.ViewStateRecord instanceof TopicViewStateRecord) {
            this.ViewStateRecord.viewStateLayout.reset();
        }
    }
    // Helper methods

    public async setViewStateRecord(vs: ViewStateRecord) {

        if (this.ViewStateRecordPropertySubscription) {
            this.ViewStateRecordPropertySubscription.unsubscribe();
        }
        if (this.SlideShowViewStateRecordSubscription) this.SlideShowViewStateRecordSubscription.unsubscribe();

        DataStore.disposeSearchResults();

        if (vs) {
            if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
                if (vs instanceof TopicViewStateRecord || vs instanceof SearchViewStateRecord || vs instanceof PublicationViewStateRecord) {
                    if (vs instanceof PublicationViewStateRecord) {
                        if (vs.viewStateLayout.Value == ViewStateLayouts.CARD) {
                            vs.viewStateLayout.Default = ViewStateLayouts.CONDENSE;
                            vs.viewStateLayout.Value = ViewStateLayouts.CONDENSE;
                        }
                    }
                    // Set default custom card fields
                    if ((!vs.viewStateCustomCardItems || !Array.isArray(vs.viewStateCustomCardItems))) {
                        vs.viewStateCustomCardItems = Object.assign([], this.customCardFieldsService.CustomCardFields);
                        (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = true;
                    } else {
                        (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = false;
                    }
                }
                this.NavigationViewStateRecord = this.ViewStateRecord = RecordFactory.createViewStateForType<SingleViewStateRecord>(vs.viewStateType);
                this.ViewStateFilterLabel = this.languageSettings.PSTWebFilterLabel;
            }
            if (vs instanceof SearchViewStateRecord) {
                this.ViewStateFilterLabel = this.languageSettings.PSTWebRefineSearchLabel;
            }
            if (vs instanceof SlideshowViewStateRecord) {
                let res = await this.getViewStateRecords();
                this.NavigationViewStateRecord = RecordFactory.createViewStateForType<SlideshowViewStateRecord>(vs.viewStateType);
                this.ViewStateRecord = null;
                this.ViewStateFilterLabel = this.languageSettings.PSTWebRefineSlideshowLabel;
            }

            vs.copyDataTo(this.NavigationViewStateRecord, true);
            if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
                (<SingleViewStateRecord>this.NavigationViewStateRecord).isViewStateCustomCardItemsBlank = (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank;
            }

            if (this.NavigationViewStateRecord && this.NavigationViewStateRecord instanceof PublicationViewStateRecord) {
                // @TODO Check in the data store cache
                // Load channel
                this.NavigationViewStateRecord.publicationChannel = null;
                if (!!this.NavigationViewStateRecord.publicationChannelGUID) {
                    let res = await this.publicationChannelsService.getPublicationChannelRecord(this.NavigationViewStateRecord.publicationChannelGUID);
                    if (res.status) {
                        this.NavigationViewStateRecord.publicationChannel = res.data;
                    }
                }
            }
        } else {
            this.NavigationViewStateRecord = null;
            this.ViewStateRecord = null;
        }

        this.NavigationViewStateRecordSubject.next(this.NavigationViewStateRecord);
        this.ViewStateRecordSubject.next(this.ViewStateRecord);

        if (this.ViewStateRecord) {
            this.ViewStateRecordPropertySubscription = this.ViewStateRecord.PropertyObservable.subscribe(this.onViewStatePropertyChanged.bind(this));
        }

        this.inSlideshowMode = false;
        // If this is a slideshow - start it
        if (this.NavigationViewStateRecord && this.NavigationViewStateRecord instanceof SlideshowViewStateRecord) {
            this.SlideShowViewStateRecordSubscription = this.slideshowService.ViewStateRecordObservable.subscribe(this.onSlideshowViewStateRecordChanged.bind(this));
            this.inSlideshowMode = true;
            this.slideshowService.initialize(this.NavigationViewStateRecord);
            this.slideshowService.start();
        }
    }

    public onSlideshowViewStateRecordChanged(vs: ViewStateRecordsAlias) {
        DataStore.disposeSearchResults();
        this.ViewStateRecord = vs;
        this.ViewStateRecordSubject.next(vs);
    }

    public onViewStatePropertyChanged(prop: [string, any, any]) {
        const field = prop[0];
        const value = prop[1];
        const prev = prop[2];

        if (field == PSTFieldNames.PSTViewStateGrouping && (<SingleViewStateRecord>this.ViewStateRecord).viewStateSorting) {
            if (value == (<SingleViewStateRecord>this.ViewStateRecord).viewStateSorting.Value) {
                (<SingleViewStateRecord>this.ViewStateRecord).viewStateSorting.Value = (<SingleViewStateRecord>this.ViewStateRecord).viewStateSorting.Default;
            }
        }
        this.ViewStateRecordPropertySubject.next(prop);
    }

    public async updateTeamCustomCardFields(fields: Array<FieldDef>, team: string): Promise<ServiceResponse<boolean>> {
        return this.customCardFieldsService.updateTeamCustomCardFields(fields, team);
    }

    public applyTeamCustomCardFields() {
        if (this.ViewStateRecord instanceof SingleViewStateRecord || this.ViewStateRecord instanceof PublicationViewStateRecord) {
            // Set default custom card fields
            if ((<SingleViewStateRecord>this.ViewStateRecord).isViewStateCustomCardItemsBlank) {
                this.ViewStateRecord.viewStateCustomCardItems = Object.assign([], this.customCardFieldsService.CustomCardFields);
            }
        }
    }

    public async createRSSFeed(vs: RSSFeedViewStateRecord): Promise<ServiceResponse<boolean>> {
        try {
            //await this.nomWebServicesJson.discoverAPILogin();
            let response = await this.nomWebServicesJson.discoverAPIPost(
                "https://usv-qa.enpscloud.com/discover/subscriptions/rss",
                {
                    "subscriptions": [
                        {
                            "FeedSetting": vs.viewStateFeedURL,
                            "feedName": vs.title
                        }
                    ]
                },
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "Authorization": this.nomWebServicesJson.token,
                        //"Authorization": this.nomWebServicesJson.discoverToken,
                        "accept": "text/plain"
                    }),
                    responseType: 'json',
                    observe: 'response'
                }
            );

            let subscriptions: Array<FeedSubscription> = Object.assign(response.body);

            if (subscriptions.length > 0) {
                vs.viewStateFeedID = subscriptions[0].feedId;
            } else {
                vs.viewStateFeedID = "";
            }
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to create rss feed!",
                detailMessage: `Failed to create rss feed! \n\rStackTrace: LayoutState.createRSSFeed(); \n\rError: ${error}`
            });
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }

    public async createTwitterFeed(vs: TwitterFeedViewStateRecord): Promise<ServiceResponse<boolean>> {
        try {
            //await this.nomWebServicesJson.discoverAPILogin();
            let response = await this.nomWebServicesJson.discoverAPIPost(
                "https://usv-qa.enpscloud.com/discover/subscriptions/twitter",
                {
                    "subscriptions": [
                        {
                            "FeedSetting": vs.viewStateFeedName,
                            "feedName": vs.title
                        }
                    ]
                },
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        //"Authorization": this.nomWebServicesJson.discoverToken,
                        "Authorization": this.nomWebServicesJson.token,
                        "accept": "text/plain"
                    }),
                    responseType: 'json',
                    observe: 'response'
                }
            );

            let subscriptions: Array<FeedSubscription> = Object.assign(response.body);

            if (subscriptions.length > 0) {
                vs.viewStateFeedID = subscriptions[0].feedId;
            } else {
                vs.viewStateFeedID = "";
            }
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to create rss feed!",
                detailMessage: `Failed to create rss feed! \n\rStackTrace: LayoutState.createRSSFeed(); \n\rError: ${error}`
            });
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }

    public async createViewStateRecord(vs: ViewStateRecord): Promise<ServiceResponse<ViewStateRecord>> {
        vs.owner = this.nomWebServicesJson.UserId;
        let database = this.nomWebServicesJson.getDatabase();
        let data = this.nomWebServicesJson.Convertor.serialize(vs);
        if (vs instanceof SingleViewStateRecord && vs.isViewStateCustomCardItemsBlank) {
            delete data[PSTFieldNames.PSTViewStateCardCustomItems];
        }
        delete data["guid"];
        delete data["Guid"];

        return this.basicContentService.createBasicContent<ViewStateRecord>(database, vs.path.fullPath, false, data, null);
    }

    public async updateViewStateRecord(vs: ViewStateRecord): Promise<ServiceResponse<ViewStateRecord>> {
        vs.owner = this.nomWebServicesJson.UserId;
        let database = this.nomWebServicesJson.getDatabase();
        let guid = vs.guid;
        let data = this.nomWebServicesJson.Convertor.serialize(vs, true);
        if (vs instanceof SingleViewStateRecord && vs.isViewStateCustomCardItemsBlank) {
            delete data[PSTFieldNames.PSTViewStateCardCustomItems];
        }
        return this.basicContentService.updateBasicContent<ViewStateRecord>(database, vs.path.fullPath, guid, false, data, false, null);
    }

    public async deleteViewStateRecord(vs: ViewStateRecord): Promise<ServiceResponse<Record>> {
        let res = await this.basicContentService.deleteBasicContent(vs.database, vs.path.fullPath, vs.guid, vs.type, null);
        return res;
    }

    protected getViewStatesParams(team?: string): any {
        let nomLocations = [];

        if (team === 'all') {
            let teams = this.globalTablesService.getListItemsForTeamLocations();
            nomLocations.push({
                BasePath: "P_" + this.globalTablesService.DefaultPlaybookTeam.ID + "\\",
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false
            });

            for (let team of teams) {

                nomLocations.push({
                    BasePath: "P_" + team.id + "\\",
                    SearchWIP: true,
                    SearchArchives: false,
                    SearchTrash: false
                });
            }
        } else {
            nomLocations = [{
                BasePath: team ? "P_" + team + "\\" : "P_" + this.nomWebServicesJson.HelloResults.HomeProgram + "\\",
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false
            }];
        }

        // In edit team settings mode don't load personal views
        nomLocations.push({
            BasePath: "U_" + this.nomWebServicesJson.UserId + "\\",
            SearchWIP: true,
            SearchArchives: false,
            SearchTrash: false
        });

        let params = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: "",
            zFields: null,
            NOMLocations: nomLocations,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: 300,
            SortByRank: false,
            ExactMatch: false,
            FieldsToExclude: null,
            FieldsToReturn: null
        };

        return params;
    }

    private getViewStateTypesByApplication(applicationName?: string): Array<number> {
        applicationName = applicationName || this.applicationName;
        if (applicationName) {
            if (applicationName == 'playbook') {
                return [ViewStateTypes.TOPIC, ViewStateTypes.ASSIGNMENT, ViewStateTypes.SEARCH, ViewStateTypes.SLIDESHOW];
            }
            if (applicationName == 'discover') {
                return [ViewStateTypes.WIRE, ViewStateTypes.WIRESEARCH];
            }
            if (applicationName == 'journal') {
                return [ViewStateTypes.DRAFT, ViewStateTypes.DRAFTSEARCH];
            }
            if (applicationName == 'live') {
                return [ViewStateTypes.ROLIST, ViewStateTypes.LIVESEARCH];
            }
            if (applicationName == 'cast') {
                return [ViewStateTypes.PUBLICATION, ViewStateTypes.CASTSEARCH];
            }
        }
        return [];
    }

    public async getViewStateRecord(guid: string): Promise<ServiceResponse<ViewStateRecord>> {

        let zFields: Array<{ FieldName: string, FieldValue: any }> = [
            { FieldName: "guid", FieldValue: guid },
            { FieldName: "type", FieldValue: 139 }
        ];

        let params = this.getViewStatesParams();
        params.zFields = zFields;
        params["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post(
                "/api/Search",
                params,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load view state!",
                detailMessage: `Failed to load view state! \n\rStackTrace: LayoutState.getViewStateRecord(); \n\rError: ${error}`
            });
            return new ServiceResponse<ViewStateRecord>(false, null);
        }

        if (response) {
            let responseJSON = response.body;
            if (responseJSON.SearchResults && responseJSON.SearchResults.length > 0) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(responseJSON.SearchResults[0]);
                let viewStateRecord: ViewStateRecord = this.nomWebServicesJson.Convertor.fromServiceViewStateRecord(serviceRecord);
                DataStore.set(viewStateRecord);
                return new ServiceResponse<ViewStateRecord>(true, viewStateRecord);
            }
        }
        return new ServiceResponse<ViewStateRecord>(false, null);
    }

    public async getPartialViewStateRecords(applicationName: string): Promise<ServiceResponse<PlanningViewsResults>> {
        let fieldsToReturn: Array<string> = ["guid", "Title", "PSTViewStateType", "PSTViewStateFilter", "PSTViewStateSorting"];
        return this.getViewStateRecords(applicationName, fieldsToReturn);
    }

    public async getViewStateRecords(applicationName?: string, fieldsToReturn?: Array<string>, team?: string, filterViewStateType?: number): Promise<ServiceResponse<PlanningViewsResults>> {
        let results: PlanningViewsResults = new PlanningViewsResults();

        applicationName = applicationName || this.applicationName;
        let viewStateTypes: Array<number> = this.getViewStateTypesByApplication(applicationName);
        let viewStateTypesFieldValue = ((filterViewStateType && filterViewStateType != -1) || filterViewStateType == 0) ? filterViewStateType : viewStateTypes.join(";")
        let zFields: Array<{ FieldName: string, FieldValue: any }> = [
            { FieldName: "virtualPath", FieldValue: "\\\\" + this.nomWebServicesJson.PSTServer },
            { FieldName: "type", FieldValue: 139 },
            { FieldName: PSTFieldNames.PSTViewStateType + "__list__", FieldValue: viewStateTypesFieldValue }
        ];

        let params = this.getViewStatesParams(team);

        params["zFields"] = zFields;
        params["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        if (fieldsToReturn && fieldsToReturn.length > 0) {
            params["FieldsToReturn"] = fieldsToReturn;
        }
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post(
                "/api/Search",
                params,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load planning views!",
                detailMessage: `Failed to load planning views! \n\rStackTrace: NomWebServicesJson.getPlanningViews(); \n\rError: ${error}`
            });
            return new ServiceResponse<PlanningViewsResults>(true, results);
        }

        if (response) {
            for (let p of response.body.SearchResults) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                try {
                    let viewStateRecord: ViewStateRecord = this.nomWebServicesJson.Convertor.fromServiceViewStateRecord(serviceRecord);
                    const label = "PSTWebView";
                    viewStateRecord.title = (this.languageSettings.get(`${label}_${serviceRecord.ListData.Path}_${serviceRecord.ListData.Title}`).includes(label)) ? serviceRecord.ListData.Title : this.languageSettings.get(`${label}_${serviceRecord.ListData.Path}_${serviceRecord.ListData.Title}`);

                    DataStore.set(viewStateRecord);
                    results.add(viewStateRecord);
                } catch (e) {
                    console.log(serviceRecord);
                    console.log(e);
                }
            }
        }

        let res = new ServiceResponse<PlanningViewsResults>(true, results);
        if (res.status) {
            DataStore.registerViewStateResults(applicationName, res.data);
        }

        return res;
    }

    public async loadViewStateRecords(group?: string, creators?: Array<string>) {
        let path;
        if (group === "all") {
            path = "all";
        } else if (group === "global") {
            path = this.globalTablesService.DefaultPlaybookTeam.ID;
        } else if (group === "personal") {
            path = this.nomWebServicesJson.UserId;
        } else {
            path = group;
        }

        await this.getViewStateRecords(undefined, undefined, path);

        if (group) {
            let res = DataStore.getViewStateResults(this.applicationName)?.views;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this.applicationName).views = res;

            res = DataStore.getViewStateResults(this.applicationName)?.searches;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this.applicationName).searches = res;

            res = DataStore.getViewStateResults(this.applicationName)?.slideshows;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this.applicationName).slideshows = res;
        }
    }

    private filterViewStateResults(group: string, creators: Array<string>, data: ViewStateRecord[]): ViewStateRecord[] {
        let res = data.filter((vs: ViewStateRecord) => {
            if (creators && creators.length) {
                if (creators.indexOf(vs.owner) < 0) return false;
            }
            if (group === "all") {
                return true;
            } else if (group === "global") {
                return (vs.path.groupId === this.globalTablesService.DefaultPlaybookTeam.ID);
            } else if (group === "personal") {
                return (vs.path.groupId === this.nomWebServicesJson.UserId);
            } else {
                return (vs.path.groupId === group);
            }
            return true;
        });
        return res;
    }
}

export class FeedSubscription {
    public subId: string;
    public userId: string;
    public feedId: string;
    public feedName: string;
    public feedType: string;
}
